import React, { useState } from 'react'
import {
  Box, Paper, Typography, TextField, Button,
  CircularProgress, Alert, InputAdornment, IconButton, Link
} from '@mui/material'
import EmailIcon from '@mui/icons-material/Email'
import LockIcon from '@mui/icons-material/Lock'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useMutation, gql } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import { staffLogin, forgotStaffPassword } from '../apollo'
import Logo from '../assets/img/rushmyfood-logo.png'

const STAFF_LOGIN = gql`${staffLogin}`
const FORGOT_STAFF_PASSWORD = gql`${forgotStaffPassword}`

function StaffLogin() {
  const history = useHistory()
  const [mode, setMode] = useState('login') // 'login' | 'forgot' | 'sent'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPass, setShowPass] = useState(false)
  const [error, setError] = useState('')

  const [mutateStaff, { loading }] = useMutation(STAFF_LOGIN, { errorPolicy: 'none' })
  const [mutateForgot, { loading: sending }] = useMutation(FORGOT_STAFF_PASSWORD, { errorPolicy: 'none' })

  const handleLogin = async () => {
    setError('')
    if (!email || !password) { setError('Please enter your email and password'); return }
    try {
      const { data } = await mutateStaff({ variables: { email: email.trim().toLowerCase(), password } })
      const s = data?.staffLogin
      if (!s) { setError('Login failed'); return }
      localStorage.setItem('staff-session', JSON.stringify(s))
      if (s.role === 'MANAGER') {
        localStorage.setItem('user-enatega', JSON.stringify({
          userId: s.userId, token: s.token, userType: 'VENDOR',
          email: s.email, name: `${s.firstName} ${s.lastName}`,
          restaurants: [{ _id: s.restaurantId, name: s.restaurantName }]
        }))
        localStorage.setItem('restaurantId', s.restaurantId)
        localStorage.setItem('restaurantName', s.restaurantName || '')
        history.replace('/admin/dashboard')
      } else {
        history.replace('/server/orders')
      }
    } catch (e) {
      setError(e.graphQLErrors?.[0]?.message || e.message || 'Invalid email or password')
    }
  }

  const handleForgot = async () => {
    setError('')
    if (!email) { setError('Please enter your email address'); return }
    try {
      await mutateForgot({ variables: { email: email.trim().toLowerCase() } })
      setMode('sent')
    } catch (e) {
      setError(e.graphQLErrors?.[0]?.message || e.message || 'Something went wrong')
    }
  }

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      '&:hover fieldset': { borderColor: '#3b82f6' },
      '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
    }
  }

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', p: 2
    }}>
      <Paper elevation={0} sx={{
        borderRadius: '20px', p: { xs: 3, sm: 4.5 }, maxWidth: 420, width: '100%',
        border: '1px solid #e2e8f0', boxShadow: '0 24px 48px rgba(0,0,0,0.2)'
      }}>
        <Box sx={{ textAlign: 'center', mb: 3.5 }}>
          <img src={Logo} alt="RushMyFood" style={{ height: 44, marginBottom: 14 }} />
          <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: '#0f172a' }}>
            {mode === 'forgot' ? 'Reset Password' : mode === 'sent' ? 'Check Your Email' : 'Staff Portal'}
          </Typography>
          <Typography sx={{ fontSize: '0.83rem', color: '#64748b', mt: 0.5 }}>
            {mode === 'forgot'
              ? 'Enter your email and we\'ll send a reset link'
              : mode === 'sent'
              ? `A password reset link has been sent to ${email}`
              : 'Sign in with your staff credentials'}
          </Typography>
        </Box>

        {/* ── Login form ── */}
        {mode === 'login' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Email Address" type="email" value={email}
              onChange={e => setEmail(e.target.value)} fullWidth size="small" sx={inputSx}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
              InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
            />
            <TextField label="Password" type={showPass ? 'text' : 'password'} value={password}
              onChange={e => setPassword(e.target.value)} fullWidth size="small" sx={inputSx}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LockIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPass(v => !v)}>
                      {showPass ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Box sx={{ textAlign: 'right', mt: -1 }}>
              <Link component="button" onClick={() => { setError(''); setMode('forgot') }}
                sx={{ fontSize: '0.8rem', color: '#3b82f6', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                Forgot Password?
              </Link>
            </Box>

            {error && <Alert severity="error" sx={{ borderRadius: '10px', fontSize: '0.83rem' }}>{error}</Alert>}

            <Button onClick={handleLogin} disabled={loading} fullWidth variant="contained"
              sx={{ py: 1.4, borderRadius: '10px', textTransform: 'none', fontWeight: 700, fontSize: '0.95rem', bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}>
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Sign In'}
            </Button>

            <Typography sx={{ textAlign: 'center', fontSize: '0.78rem', color: '#94a3b8' }}>
              Restaurant owner?{' '}
              <Link href="/#/auth/login" sx={{ color: '#3b82f6', fontWeight: 600, textDecoration: 'none' }}>
                Admin Login
              </Link>
            </Typography>
          </Box>
        )}

        {/* ── Forgot password form ── */}
        {mode === 'forgot' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Email Address" type="email" value={email}
              onChange={e => setEmail(e.target.value)} fullWidth size="small" sx={inputSx}
              onKeyDown={e => e.key === 'Enter' && handleForgot()}
              InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
            />

            {error && <Alert severity="error" sx={{ borderRadius: '10px', fontSize: '0.83rem' }}>{error}</Alert>}

            <Button onClick={handleForgot} disabled={sending} fullWidth variant="contained"
              sx={{ py: 1.4, borderRadius: '10px', textTransform: 'none', fontWeight: 700, bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}>
              {sending ? <CircularProgress size={22} color="inherit" /> : 'Send Reset Link'}
            </Button>

            <Button startIcon={<ArrowBackIcon />} onClick={() => { setError(''); setMode('login') }}
              sx={{ textTransform: 'none', fontSize: '0.82rem', color: '#64748b' }}>
              Back to Sign In
            </Button>
          </Box>
        )}

        {/* ── Sent confirmation ── */}
        {mode === 'sent' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'center' }}>
            <Alert severity="success" sx={{ borderRadius: '10px', fontSize: '0.83rem' }}>
              Check your inbox and click the link to set a new password.
            </Alert>
            <Button onClick={() => { setError(''); setMode('login') }} fullWidth variant="outlined"
              sx={{ py: 1.4, borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
              Back to Sign In
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  )
}

export default StaffLogin
